import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import ProcesosMenu from './common/ProcesosMenu';
import SolicitudesProcesosBadge from './common/SolicitudesProcesosBadge';
import UserAccountMenu from './common/UserAccountMenu';
import LoadingSpinner from './common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import ProcesosService from '../services/ProcesosService';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import {
  isAdminUser,
  isProfesor as isProfesorUser,
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import BaseMenuOptions from './sidebar/BaseMenuOptions';
import { navigateAdminMenuItem, navigateProfessorMenuItem } from './sidebar/menuConfig';
import './NewDashboard.css';
import './AdminDashboard.css';
import './Procesos.css';

const PAGE_SIZE = 20;

const fallbackMenuItems = [
  { id: 'inicio', label: 'Inicio', icon: 'fas fa-home' },
  { id: 'procesos', label: 'Procesos', icon: 'fas fa-tasks' }
];

const normalizeEstado = (value) => String(value || '').trim().toLowerCase();

const getProcesoStatus = (proceso) => {
  const estado = normalizeEstado(proceso?.estado);

  if (proceso?.error || estado.includes('error')) {
    return { label: 'Error', className: 'error', icon: 'fas fa-exclamation-triangle' };
  }
  if (estado.includes('final') || proceso?.fechaFinalizacion) {
    return { label: 'Finalizado', className: 'finalizado', icon: 'fas fa-check-circle' };
  }
  if (estado.includes('encol')) {
    return { label: 'Encolado', className: 'encolado', icon: 'fas fa-clock' };
  }
  if (estado.includes('proces')) {
    return { label: 'Procesando', className: 'procesando', icon: 'fas fa-spinner fa-spin' };
  }
  return { label: 'En proceso', className: 'en-proceso', icon: 'fas fa-hourglass-half' };
};

const getProgressValue = (proceso) => {
  const value = Number(proceso?.porcentajeAvance ?? 0);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-AR');
};

const parseBodyMensaje = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const renderValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatBodyKey = (key) => {
  const normalized = String(key || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getBodyEntries = (body) => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return [];
  const hiddenKeys = new Set(['archivos', 'Archivos', 'files', 'Files']);
  return Object.entries(body)
    .filter(([key, value]) => !hiddenKeys.has(key) && value !== null && value !== undefined && value !== '')
    .slice(0, 16);
};

const getBodyFiles = (body) => {
  if (!body || typeof body !== 'object') return [];
  const files = body.archivos || body.Archivos || body.files || body.Files || [];
  return Array.isArray(files) ? files : [];
};

const getFileDisplayName = (file, index) => {
  if (!file || typeof file !== 'object') {
    return `Archivo ${index + 1}`;
  }

  const value = file.nombreOriginal ||
    file.NombreOriginal ||
    file.originalName ||
    file.OriginalName ||
    file.nombreArchivo ||
    file.NombreArchivo ||
    file.fileName ||
    file.FileName ||
    file.filename ||
    file.Filename ||
    file.nombre ||
    file.Nombre ||
    file.name ||
    file.Name;

  return String(value || '').trim() || `Archivo ${index + 1}`;
};

const getFileSizeBytes = (file) => {
  const bytes = Number(file?.length || file?.Length || file?.size || file?.Size);
  return Number.isFinite(bytes) && bytes > 0 ? bytes : null;
};

const formatFileSizeMb = (bytes) => {
  if (!bytes) return null;
  const megabytes = bytes / (1024 * 1024);
  if (megabytes > 0 && megabytes < 0.01) {
    return '< 0,01 MB';
  }

  return `${megabytes.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} MB`;
};

const Procesos = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminUser(user);
  const isProfesor = isProfesorUser(user);
  const { featureFlags } = useRegionalFeatureFlags(user, { enabled: Boolean(user) && !authLoading });
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);

  const [procesos, setProcesos] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [confirmStopId, setConfirmStopId] = useState('');
  const [stoppingId, setStoppingId] = useState('');
  const [stopError, setStopError] = useState('');
  const [confirmRetryId, setConfirmRetryId] = useState('');
  const [retryingId, setRetryingId] = useState('');
  const [retryError, setRetryError] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadProcesos = useCallback(async ({ silent = false, nextPage = page } = {}) => {
    if (authLoading || !user) return;

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const response = await ProcesosService.getProcesos({ page: nextPage, pageSize: PAGE_SIZE });
      if (!response?.ok) {
        throw new Error(response?.mensaje || 'No pudimos cargar los procesos.');
      }

      const nextProcesos = response.procesos || [];
      setProcesos(nextProcesos);
      setTotal(response.total || 0);
      setUnreadCount(response.unreadCount || 0);
      setPage(response.page || nextPage);
    } catch (loadError) {
      setError(loadError.message || 'No pudimos cargar los procesos.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authLoading, page, user]);

  useEffect(() => {
    loadProcesos({ nextPage: page });
  }, [loadProcesos, page]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadProcesos({ silent: true, nextPage: page });
    }, 15000);

    return () => window.clearInterval(interval);
  }, [loadProcesos, page]);

  useEffect(() => {
    if (!procesos.length) {
      setSelectedId('');
      return;
    }

    if (!selectedId || !procesos.some((proceso) => proceso.id === selectedId)) {
      setSelectedId(procesos[0].id);
    }
  }, [procesos, selectedId]);

  const filteredProcesos = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return procesos.filter((proceso) => {
      const status = getProcesoStatus(proceso).className;
      const matchesStatus = statusFilter === 'todos' || status === statusFilter;
      const matchesQuery = !normalizedQuery || [
        proceso.descripcion,
        proceso.detalle,
        proceso.error,
        proceso.bodyMensaje
      ].some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
      return matchesStatus && matchesQuery;
    });
  }, [procesos, query, statusFilter]);

  const selectedProceso = useMemo(() => {
    if (!filteredProcesos.length) return null;
    return filteredProcesos.find((proceso) => proceso.id === selectedId) || filteredProcesos[0];
  }, [filteredProcesos, selectedId]);

  const selectedBody = useMemo(() => parseBodyMensaje(selectedProceso?.bodyMensaje), [selectedProceso]);
  const selectedBodyEntries = useMemo(() => getBodyEntries(selectedBody), [selectedBody]);
  const selectedFiles = useMemo(() => getBodyFiles(selectedBody), [selectedBody]);

  const handleMenuSelect = (item) => {
    setIsSidebarOpen(false);
    if (!item?.id || item.id === 'procesos') return;
    if (isAdmin) {
      navigateAdminMenuItem(navigate, item.id);
      return;
    }
    if (isProfesor) {
      navigateProfessorMenuItem(navigate, item.id);
      return;
    }
    if (item.id === 'inicio') {
      navigate('/');
    }
  };

  const handleRefresh = () => {
    loadProcesos({ silent: true, nextPage: page });
  };

  const handleSelectProceso = async (proceso) => {
    setSelectedId(proceso.id);
    if (proceso.leido) return;

    try {
      await ProcesosService.marcarLeido(proceso.id);
      setProcesos((prev) => prev.map((item) => (item.id === proceso.id ? { ...item, leido: true } : item)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (markError) {
      console.error('Error al marcar proceso como leído:', markError);
    }
  };

  const canStopProceso = (proceso) => {
    const status = getProcesoStatus(proceso).className;
    return status === 'encolado' || status === 'procesando';
  };

  const handleStopProceso = async (proceso) => {
    setStoppingId(proceso.id);
    setStopError('');
    try {
      const response = await ProcesosService.detener(proceso.id);
      if (response?.ok === false) {
        throw new Error(response?.mensaje || 'No pudimos detener el proceso.');
      }
      setConfirmStopId('');
      await loadProcesos({ silent: true, nextPage: page });
    } catch (stopErr) {
      setStopError(stopErr.message || 'No pudimos detener el proceso.');
    } finally {
      setStoppingId('');
    }
  };

  const canRetryProceso = (proceso) => {
    if (getProcesoStatus(proceso).className !== 'error') return false;
    const body = parseBodyMensaje(proceso?.bodyMensaje);
    return String(body?.Evento || body?.evento || '').toLowerCase() === 'creacionagenteave';
  };

  const handleRetryProceso = async (proceso) => {
    setRetryingId(proceso.id);
    setRetryError('');
    try {
      const response = await ProcesosService.reintentar(proceso.id);
      if (response?.ok === false) {
        throw new Error(response?.mensaje || 'No pudimos reintentar el proceso.');
      }
      setConfirmRetryId('');
      await loadProcesos({ silent: true, nextPage: page });
    } catch (retryErr) {
      setRetryError(retryErr.message || 'No pudimos reintentar el proceso.');
    } finally {
      setRetryingId('');
    }
  };

  const renderSidebarMenu = () => {
    if (isAdmin) {
      return (
        <AdminRoleMenuOptions
          user={user}
          isGptClassicEnabled={isGptClassicEnabled}
          isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
          isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
          activeItemId="procesos"
          onSelect={handleMenuSelect}
        />
      );
    }

    if (isProfesor) {
      return (
        <ProfessorMenuOptions
          user={user}
          isGptClassicEnabled={isGptClassicEnabled}
          isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
          isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
          activeItemId="procesos"
          onSelect={handleMenuSelect}
        />
      );
    }

    return (
      <BaseMenuOptions
        items={fallbackMenuItems}
        activeItemId="procesos"
        onSelect={handleMenuSelect}
      />
    );
  };

  if (authLoading || loading) {
    return (
      <div className="procesos-loading">
        <LoadingSpinner message="Cargando procesos..." />
      </div>
    );
  }

  return (
    <div className={`new-dashboard procesos-page ${isAdmin ? 'admin-mode' : ''} ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className={isAdmin ? 'fas fa-university' : 'fas fa-graduation-cap'}></i>
            <span>{isAdmin ? 'Admin' : 'UTN FRC'}</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {renderSidebarMenu()}
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className={isAdmin ? 'fas fa-user-shield' : 'fas fa-user'}></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.roleName || user?.rol || user?.role || 'Usuario'}</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Alternar menú lateral"
            >
              <i className="fas fa-bars"></i>
            </button>
            <div>
              <h1 className="page-title">Procesos</h1>
              <p className="page-subtitle">Seguimiento de tareas encoladas y creación de asistentes AVE.</p>
            </div>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            {isAdmin && <SolicitudesProcesosBadge />}
            <ProcesosMenu />
            <UserAccountMenu />
          </div>
        </header>

        <div className="dashboard-content procesos-content">
          <div className="procesos-toolbar">
            <div className="procesos-search">
              <i className="fas fa-search"></i>
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar por descripción, detalle o error"
              />
            </div>
            <div className="procesos-filter-group" aria-label="Filtrar procesos por estado">
              {[
                { id: 'todos', label: 'Todos' },
                { id: 'encolado', label: 'Encolados' },
                { id: 'procesando', label: 'Procesando' },
                { id: 'finalizado', label: 'Finalizados' },
                { id: 'error', label: 'Errores' }
              ].map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  className={`procesos-filter-btn ${statusFilter === filter.id ? 'active' : ''}`}
                  onClick={() => setStatusFilter(filter.id)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="procesos-refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
              <span>Actualizar</span>
            </button>
          </div>

          {error && (
            <div className="procesos-error">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          )}

          <div className="procesos-summary-row">
            <div className="procesos-summary-item">
              <span className="summary-label">Total</span>
              <span className="summary-value">{total}</span>
            </div>
            <div className="procesos-summary-item">
              <span className="summary-label">No leídos</span>
              <span className="summary-value">{unreadCount}</span>
            </div>
            <div className="procesos-summary-item">
              <span className="summary-label">Página</span>
              <span className="summary-value">{page}/{totalPages}</span>
            </div>
          </div>

          <div className="procesos-layout">
            <section className="procesos-list-panel">
              <div className="procesos-list-header">
                <span>Procesos recientes</span>
                <span>{filteredProcesos.length}</span>
              </div>

              <div className="procesos-list-scroll">
                {filteredProcesos.length === 0 ? (
                  <div className="procesos-empty-state">
                    <i className="fas fa-inbox"></i>
                    <span>No hay procesos para mostrar.</span>
                  </div>
                ) : (
                  filteredProcesos.map((proceso) => {
                    const status = getProcesoStatus(proceso);
                    const progress = getProgressValue(proceso);
                    return (
                      <button
                        type="button"
                        key={proceso.id}
                        className={`procesos-row ${selectedProceso?.id === proceso.id ? 'active' : ''} ${proceso.leido ? '' : 'unread'}`}
                        onClick={() => handleSelectProceso(proceso)}
                      >
                        <div className="procesos-row-title">
                          <span>{proceso.descripcion || 'Proceso'}</span>
                          <span className={`procesos-status ${status.className}`}>
                            <i className={status.icon}></i>
                            {status.label}
                          </span>
                        </div>
                        <div className="procesos-row-detail">{proceso.detalle || proceso.error || 'Pendiente de actualización.'}</div>
                        <div className="procesos-progress-line">
                          <div className="procesos-progress-track">
                            <div className={`procesos-progress-fill ${status.className}`} style={{ width: `${progress}%` }}></div>
                          </div>
                          <span>{progress}%</span>
                        </div>
                        <div className="procesos-row-date">
                          <span>{formatDate(proceso.fechaCreacion)}</span>
                          {proceso.fechaActualizacion && <span>Actualizado: {formatDate(proceso.fechaActualizacion)}</span>}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="procesos-pagination">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page <= 1}
                  title="Página anterior"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span>{page} de {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                  title="Página siguiente"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </section>

            <section className="procesos-detail-panel">
              {!selectedProceso ? (
                <div className="procesos-empty-detail">
                  <i className="fas fa-tasks"></i>
                  <span>Selecciona un proceso para ver el detalle.</span>
                </div>
              ) : (
                <>
                  {(() => {
                    const status = getProcesoStatus(selectedProceso);
                    const progress = getProgressValue(selectedProceso);
                    return (
                      <div className="procesos-detail-header">
                        <div>
                          <h2>{selectedProceso.descripcion || 'Proceso'}</h2>
                          <span className={`procesos-status ${status.className}`}>
                            <i className={status.icon}></i>
                            {status.label}
                          </span>
                        </div>
                        <div className="procesos-detail-progress">
                          <span>{progress}%</span>
                          <div className="procesos-progress-track">
                            <div className={`procesos-progress-fill ${status.className}`} style={{ width: `${progress}%` }}></div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {canStopProceso(selectedProceso) && (
                    <div className="procesos-stop-block">
                      {confirmStopId === selectedProceso.id ? (
                        <>
                          <span>
                            ¿Detener este proceso? Se marcará con error y la cola continuará con el siguiente.
                          </span>
                          <div className="procesos-stop-actions">
                            <button
                              type="button"
                              className="procesos-stop-btn confirm"
                              onClick={() => handleStopProceso(selectedProceso)}
                              disabled={stoppingId === selectedProceso.id}
                            >
                              <i className={stoppingId === selectedProceso.id ? 'fas fa-spinner fa-spin' : 'fas fa-stop-circle'}></i>
                              <span>Sí, detener</span>
                            </button>
                            <button
                              type="button"
                              className="procesos-stop-btn cancel"
                              onClick={() => { setConfirmStopId(''); setStopError(''); }}
                              disabled={stoppingId === selectedProceso.id}
                            >
                              Cancelar
                            </button>
                          </div>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="procesos-stop-btn"
                          onClick={() => { setConfirmStopId(selectedProceso.id); setStopError(''); }}
                        >
                          <i className="fas fa-stop-circle"></i>
                          <span>Detener proceso</span>
                        </button>
                      )}
                      {stopError && <div className="procesos-stop-error">{stopError}</div>}
                    </div>
                  )}

                  {canRetryProceso(selectedProceso) && (
                    <div className="procesos-retry-block">
                      {confirmRetryId === selectedProceso.id ? (
                        <>
                          <span>
                            ¿Volver a procesar desde el inicio con los mismos archivos? Se encolará detrás de los procesos pendientes.
                          </span>
                          <div className="procesos-stop-actions">
                            <button
                              type="button"
                              className="procesos-retry-btn confirm"
                              onClick={() => handleRetryProceso(selectedProceso)}
                              disabled={retryingId === selectedProceso.id}
                            >
                              <i className={retryingId === selectedProceso.id ? 'fas fa-spinner fa-spin' : 'fas fa-redo'}></i>
                              <span>Sí, reintentar</span>
                            </button>
                            <button
                              type="button"
                              className="procesos-stop-btn cancel"
                              onClick={() => { setConfirmRetryId(''); setRetryError(''); }}
                              disabled={retryingId === selectedProceso.id}
                            >
                              Cancelar
                            </button>
                          </div>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="procesos-retry-btn"
                          onClick={() => { setConfirmRetryId(selectedProceso.id); setRetryError(''); }}
                        >
                          <i className="fas fa-redo"></i>
                          <span>Reintentar desde cero</span>
                        </button>
                      )}
                      {retryError && <div className="procesos-stop-error">{retryError}</div>}
                    </div>
                  )}

                  <div className="procesos-detail-grid">
                    <div>
                      <span className="detail-label">Creado</span>
                      <span className="detail-value">{formatDate(selectedProceso.fechaCreacion)}</span>
                    </div>
                    <div>
                      <span className="detail-label">Actualizado</span>
                      <span className="detail-value">{formatDate(selectedProceso.fechaActualizacion)}</span>
                    </div>
                    <div>
                      <span className="detail-label">Finalizado</span>
                      <span className="detail-value">{formatDate(selectedProceso.fechaFinalizacion)}</span>
                    </div>
                    <div>
                      <span className="detail-label">Leído</span>
                      <span className="detail-value">{selectedProceso.leido ? 'Sí' : 'No'}</span>
                    </div>
                  </div>

                  {selectedProceso.detalle && (
                    <div className="procesos-message-block">
                      <span className="detail-label">Detalle actual</span>
                      <p>{selectedProceso.detalle}</p>
                    </div>
                  )}

                  {selectedProceso.error && (
                    <div className="procesos-message-block error">
                      <span className="detail-label">Error</span>
                      <p>{selectedProceso.error}</p>
                    </div>
                  )}

                  {selectedBodyEntries.length > 0 && (
                    <div className="procesos-metadata">
                      <h3>Datos del proceso</h3>
                      <div className="procesos-metadata-grid">
                        {selectedBodyEntries.map(([key, value]) => (
                          <div key={key} className="metadata-item">
                            <span className="detail-label">{formatBodyKey(key)}</span>
                            <span className="detail-value">{renderValue(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedFiles.length > 0 && (
                    <div className="procesos-files">
                      <h3>Archivos</h3>
                      <div className="procesos-files-list">
                        {selectedFiles.map((file, index) => {
                          const fileName = getFileDisplayName(file, index);
                          const fileSize = formatFileSizeMb(getFileSizeBytes(file));
                          return (
                            <div key={`${fileName}-${index}`} className="procesos-file-row">
                              <i className="fas fa-file-pdf"></i>
                              <span>{fileName}</span>
                              {fileSize && <small>{fileSize}</small>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="procesos-raw">
                    <h3>Mensaje</h3>
                    <pre>{selectedProceso.bodyMensaje || '-'}</pre>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Procesos;
